import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Calendar as CalendarIcon, 
  Clock,
  MapPin,
  Plus, 
  Pencil, 
  Trash2, 
  Loader2,
  Zap,
  Star,
  Trophy,
  Ticket
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Live Badge
const LiveBadge = () => (
  <Badge className="bg-red-500 text-white animate-pulse">
    <span className="w-1.5 h-1.5 bg-white rounded-full mr-1"></span>
    LIVE
  </Badge>
);

export default function FixturesAdminPage() {
  const [searchParams] = useSearchParams();
  const tournamentFilter = searchParams.get('tournament');
  const { token } = useAuth();
  
  const [fixtures, setFixtures] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTournament, setSelectedTournament] = useState(tournamentFilter || 'all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFixture, setEditingFixture] = useState(null);
  const [formData, setFormData] = useState({
    tournament_id: '',
    home_team: '',
    away_team: '',
    date: '',
    time: '',
    venue: '',
    ticket_price: '',
    total_tickets: '1000',
    is_live: false,
    is_featured: false,
    description: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTournaments();
    fetchFixtures();
  }, [selectedTournament]);

  const fetchTournaments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tournaments?active_only=false`);
      setTournaments(response.data);
    } catch (error) {
      console.error('Failed to fetch tournaments:', error);
    }
  };

  const fetchFixtures = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/api/fixtures?limit=100`;
      if (selectedTournament && selectedTournament !== 'all') {
        url += `&tournament_id=${selectedTournament}`;
      }
      const response = await axios.get(url);
      setFixtures(response.data);
    } catch (error) {
      console.error('Failed to fetch fixtures:', error);
      toast.error('Failed to load fixtures');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingFixture(null);
    setFormData({
      tournament_id: selectedTournament !== 'all' ? selectedTournament : '',
      home_team: '',
      away_team: '',
      date: '',
      time: '',
      venue: '',
      ticket_price: '',
      total_tickets: '1000',
      is_live: false,
      is_featured: false,
      description: '',
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (fixture) => {
    setEditingFixture(fixture);
    setFormData({
      tournament_id: fixture.tournament_id,
      home_team: fixture.home_team,
      away_team: fixture.away_team,
      date: fixture.date,
      time: fixture.time,
      venue: fixture.venue,
      ticket_price: fixture.ticket_price.toString(),
      total_tickets: fixture.total_tickets.toString(),
      is_live: fixture.is_live,
      is_featured: fixture.is_featured,
      description: fixture.description || '',
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.tournament_id || !formData.home_team || !formData.away_team || !formData.date || !formData.time || !formData.venue || !formData.ticket_price) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...formData,
        ticket_price: parseFloat(formData.ticket_price),
        total_tickets: parseInt(formData.total_tickets),
      };

      if (editingFixture) {
        await axios.put(
          `${API_URL}/api/fixtures/${editingFixture.id}`,
          payload,
          { headers }
        );
        toast.success('Fixture updated successfully');
      } else {
        await axios.post(`${API_URL}/api/fixtures`, payload, { headers });
        toast.success('Fixture created successfully');
      }

      setIsDialogOpen(false);
      fetchFixtures();
    } catch (error) {
      console.error('Failed to save fixture:', error);
      toast.error(error.response?.data?.detail || 'Failed to save fixture');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (fixture) => {
    if (!window.confirm(`Delete "${fixture.home_team} vs ${fixture.away_team}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/fixtures/${fixture.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Fixture deleted');
      fetchFixtures();
    } catch (error) {
      toast.error('Failed to delete fixture');
    }
  };

  const toggleLive = async (fixture) => {
    try {
      await axios.post(
        `${API_URL}/api/fixtures/${fixture.id}/toggle-live`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Fixture is now ${fixture.is_live ? 'not live' : 'LIVE'}`);
      fetchFixtures();
    } catch (error) {
      toast.error('Failed to toggle live status');
    }
  };

  const toggleFeatured = async (fixture) => {
    try {
      await axios.put(
        `${API_URL}/api/fixtures/${fixture.id}`,
        { is_featured: !fixture.is_featured },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Fixture ${fixture.is_featured ? 'unfeatured' : 'featured'}`);
      fetchFixtures();
    } catch (error) {
      toast.error('Failed to update fixture');
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <CalendarIcon className="w-7 h-7 text-[#0055FF]" />
              Fixtures
            </h1>
            <p className="text-gray-500 mt-1">Manage match fixtures and schedules</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedTournament} onValueChange={setSelectedTournament}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by tournament" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tournaments</SelectItem>
                {tournaments.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    <span className="flex items-center gap-2">
                      <span>{t.flag}</span>
                      <span>{t.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={openCreateDialog} className="bg-[#0055FF] hover:bg-[#0044CC]">
              <Plus className="w-4 h-4 mr-2" />
              Add Fixture
            </Button>
          </div>
        </div>

        {/* Fixtures List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#0055FF] animate-spin" />
          </div>
        ) : fixtures.length > 0 ? (
          <div className="space-y-4">
            {fixtures.map((fixture) => (
              <Card key={fixture.id} className={`overflow-hidden ${!fixture.is_active ? 'opacity-60' : ''}`}>
                <CardContent className="p-0">
                  <div className="flex flex-col lg:flex-row">
                    {/* Match Info */}
                    <div className="flex-1 p-4 lg:p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-lg">{fixture.tournament_flag}</span>
                        <span className="text-sm text-gray-500">{fixture.tournament_name}</span>
                        {fixture.is_live && <LiveBadge />}
                        {fixture.is_featured && (
                          <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                            <Star className="w-3 h-3 mr-1" />
                            Featured
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center justify-between mb-4">
                        <div className="flex-1 text-center">
                          <p className="font-bold text-lg text-gray-900">{fixture.home_team}</p>
                        </div>
                        <div className="px-4">
                          <span className="text-xl font-bold text-gray-400">VS</span>
                        </div>
                        <div className="flex-1 text-center">
                          <p className="font-bold text-lg text-gray-900">{fixture.away_team}</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="w-4 h-4" />
                          {fixture.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {fixture.time}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {fixture.venue}
                        </span>
                      </div>
                    </div>

                    {/* Price & Actions */}
                    <div className="flex flex-row lg:flex-col items-center justify-between lg:justify-center gap-4 p-4 lg:p-6 bg-gray-50 border-t lg:border-t-0 lg:border-l">
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Ticket Price</p>
                        <p className="text-2xl font-bold text-[#0055FF]">D{fixture.ticket_price}</p>
                        <p className="text-xs text-gray-400">
                          <Ticket className="w-3 h-3 inline mr-1" />
                          {fixture.tickets_sold || 0}/{fixture.total_tickets} sold
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => toggleLive(fixture)}
                          className={fixture.is_live ? 'text-red-500 border-red-500' : ''}
                          title={fixture.is_live ? 'End Live' : 'Go Live'}
                        >
                          <Zap className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => toggleFeatured(fixture)}
                          className={fixture.is_featured ? 'text-yellow-500 border-yellow-500' : ''}
                          title={fixture.is_featured ? 'Unfeature' : 'Feature'}
                        >
                          <Star className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="icon" onClick={() => openEditDialog(fixture)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="icon" onClick={() => handleDelete(fixture)} className="text-red-500 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-16 text-center">
              <CalendarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Fixtures Yet</h3>
              <p className="text-gray-500 mb-6">Create your first fixture to get started</p>
              <Button onClick={openCreateDialog} className="bg-[#0055FF]">
                <Plus className="w-4 h-4 mr-2" />
                Add Fixture
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingFixture ? 'Edit Fixture' : 'Create Fixture'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Tournament *</label>
                <Select
                  value={formData.tournament_id}
                  onValueChange={(value) => setFormData({ ...formData, tournament_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select tournament" />
                  </SelectTrigger>
                  <SelectContent>
                    {tournaments.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        <span className="flex items-center gap-2">
                          <span>{t.flag}</span>
                          <span>{t.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Home Team *</label>
                  <Input
                    placeholder="e.g., Arsenal"
                    value={formData.home_team}
                    onChange={(e) => setFormData({ ...formData, home_team: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Away Team *</label>
                  <Input
                    placeholder="e.g., Chelsea"
                    value={formData.away_team}
                    onChange={(e) => setFormData({ ...formData, away_team: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Date *</label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Time *</label>
                  <Input
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Venue *</label>
                <Input
                  placeholder="e.g., Emirates Stadium"
                  value={formData.venue}
                  onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Ticket Price (D) *</label>
                  <Input
                    type="number"
                    placeholder="e.g., 500"
                    value={formData.ticket_price}
                    onChange={(e) => setFormData({ ...formData, ticket_price: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Total Tickets</label>
                  <Input
                    type="number"
                    placeholder="e.g., 1000"
                    value={formData.total_tickets}
                    onChange={(e) => setFormData({ ...formData, total_tickets: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Description</label>
                <Input
                  placeholder="Optional description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_live}
                    onChange={(e) => setFormData({ ...formData, is_live: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <Zap className="w-4 h-4 text-red-500" />
                  <span className="text-sm">Live Now</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <Star className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm">Featured</span>
                </label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#0055FF]">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingFixture ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
